import { db } from "@utils/IndexedDb";
import { getQuarter } from "@utils/dateUtils";
import { showMicroComponentsFeatureFlag } from "@utils/default";
import { slugify } from "@utils/string";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import { FC, Fragment, useContext, useEffect, useState } from "react";

import AddToFavorites from "@/components/AddToFavorites/AddToFavorites";
import FloorPlan from "@/components/AvailableUnitsList/FloorPlan";
import { useNotification } from "@/components/NotificationProvider/NotificationProvider";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { projectlocale } from "@/constants/locale";
import { AvailableUnitContext } from "@/context/AvailableUnitListContext";
import { LoadingContext } from "@/context/LoaderContext";
import MagnifyContextProvider from "@/context/MagnifyContext";
import { ModalsContext } from "@/context/ModalsContext";
import { NavigationContext } from "@/context/NavigationContext";
import { SelectUnitContext } from "@/context/SelectUnitContext";
import { UserContext } from "@/context/UserContext";
import { ctaClickTracking, ecomInitiateCheckout } from "@/dataLayer/dataLayer";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import useResponsiveLabel from "@/hooks/useResponsiveLabel";
import { CommercetoolsUnit } from "@/types";

import { useCheckout } from "../Checkout/CheckoutProvider";
import "./UnitDetails.css";
import { getAvailability } from "./action";

interface Props {
  unit: CommercetoolsUnit;
  closeHandler?: () => void;
  customClass?: string;
  totalUnits?: number;
  isBelongToCommunity?: boolean | void;
  matchedCommunity?: any;
}
const attributes = ["project", "identifier", "unitType", "areaSqft", "areaSqmt", "status", "isFurnished", "balcony", "bedrooms"];
// const attributes = ["name",project", "identifier", "unitType", "areaSqft", "areaSqmt", "status", "isFurnished", "balcony", "bedrooms"];

const UnitDetails: FC<Props> = ({ unit, totalUnits, customClass, isBelongToCommunity, matchedCommunity }) => {
  const { projectUrl, filteredResults } = useContext(AvailableUnitContext);

  const t = useTranslations();
  const router = useRouter();
  const { data: session } = useSession();
  const { showUnavailableUnit } = useNotification();
  const locale = useLocale();
  const [displayGallery, setDisplayGallery] = useState<boolean>(false);
  const [displayPanorama, setDisplayPanorama] = useState<boolean>(false);
  const [displayTour, setDisplayTour] = useState<boolean>(false);
  const { loading } = useContext(LoadingContext);
  const { setIsDialogueOpen } = useContext(UserContext);
  const params = useParams<any>();
  // const { unitTitle } = useContext(SelectUnitContext);
  // console.log("isBelongToCommunity", isBelongToCommunity, matchedCommunity);
  const [language] = locale.split("-");
  const { setEnquireModalOpen, setGetFormPageLocation } = useContext(ModalsContext);
  const [translatedProjectName, setTranslatedProjectName] = useState("");

  const isSo = useIsSaleScreen();

  const enquireLabel = useResponsiveLabel(t("stickybar.mobile.enquire"), t("stickybar.desktop.enquire"));

  const handleReserve = async (inventoryID: string, id: string) => {
    loading(true);
    const response = await getAvailability(inventoryID, id);
    if (response) {
      const projectTitle = unit.project?.replaceAll(" ", "-").toLowerCase();
      const projectName = unit.propertyName?.replaceAll(" ", "-").toLowerCase();
      const productIdentifier = `${projectTitle ?? ""}/${projectName ?? ""}`;
      ecomInitiateCheckout({
        cta_name: t("unit.reserve.now"),
        form_name: "online_reservation",
        property_name: unit.project,
        project_location: `${unit.area}, ${unit.city}`,
        property_type: unit.identifier,
        bedroom_numbers: unit.bedrooms,
        bathrooms: "no of bathrooms given", // TODO fixme when data will come
        unit_id: unit.name,
        product_identifier: productIdentifier,
        unit_type: unit.unitType,
        unit_category: unit.isFurnished ? "furnished" : "unfurnished",
        unit_size: unit.areaSqft,
        unit_price: unit.price,
        currency: unit.currency,
        handover_qaurter_year: getQuarter(unit.deliveryDate, locale, t),
        status: unit.status,
        user_logged_in: !!session,
        // user_id: session?.user.id || "",
      });

      router.push(`/${locale}/checkout?step=contact`, { scroll: true });
    } else {
      loading(false);
      ctaClickTracking("reserve", "unit page", "property unavailable");
      showUnavailableUnit(filteredResults.length <= 1 ? projectUrl : undefined);
      // showNotification(t("reservation.label.unitUnavailable"), 5000, NotificationType.ERROR);
    }
  };

  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const { desktopPanelID } = useContext(NavigationContext);

  const share = async (shareData: any) => {
    const unitName = unit?.name.replace(/\//g, "-");
    const projectName = unit?.project;
    const bedroomsIdentifier = `${unit?.bedrooms ? unit?.bedrooms : unit?.unitType} + " " + ${unit?.identifier}`;
    let shareUrl;
    if (unitName && projectName) {
      // shareUrl = `/${locale}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${unitName}/`;
      shareUrl =
        isBelongToCommunity && matchedCommunity
          ? `/${locale}/communities/${matchedCommunity.slug}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${unitName}/`
          : `/${locale}/projects/${slugify(projectName)}/properties/${slugify(bedroomsIdentifier)}/${unitName}/`;
    }

    if (navigator.share) {
      shareData.url = shareUrl;
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error(err);
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert(t("copy.link"));
      console.log("share not supported");
    }
  };

  useEffect(() => {
    const pathName = params?.unit ?? "";
    const unitId = document.querySelector(`div[unit-name='${pathName}']`)?.getAttribute("unit-id");
    const targetElement = document.getElementById(`accordion__heading-${unitId}`);
    const accordionPanel = document.getElementById(`accordion__panel-${unitId}`);

    if (targetElement && accordionPanel && (totalUnits || 0) > 0) {
      targetElement.setAttribute("aria-expanded", "true");
      accordionPanel.removeAttribute("hidden");
      if (isDesktop) {
        window.scrollTo({
          top: accordionPanel?.getBoundingClientRect()?.top - 150,
          behavior: "smooth",
        });
      }
      // BUG 93492
      if (isMobile) {
        console.log("test mobile:", unitId);

        const unitButton = document.getElementById(`mobile-button-${unitId}`);
        if (unitButton) {
          window.scrollTo({
            top: unitButton.offsetTop + (window?.outerHeight - 165),
            behavior: "smooth",
          });
          unitButton.click();
        }
      }
    }
  }, [isDesktop, totalUnits, unit.id, params?.unit, isMobile]);

  const getTranslatedData = async (id: string, lang: string | "", data: any) => {
    const table = db.PROJECTS;
    const rawValues = await table.toArray();
    const product = rawValues.filter((item) => item.assignedCategory === id);
    const translation = projectlocale.find((item) => item.id === product[0].id);
    if (translation) {
      return translation?.name[lang as keyof typeof translation.name] || data?.name;
    }
  };
  // const trandata = language !== "en" ? getTranslatedData(unit.projectID, language, unit) : unit;
  useEffect(() => {
    if (language !== "en" && unit.projectID) {
      getTranslatedData(unit.projectID, language, unit).then((name) => {
        if (name) setTranslatedProjectName(name);
      });
    } else {
      setTranslatedProjectName(unit?.name || "");
    }
  }, [language, unit?.name]);

  return (
    <>
      <div className={customClass}>
        <div className="md:grid md:grid-cols-2 md:gap-4xl grow px-m flex flex-col gap-xl md:px-[56px] md:py-l">
          <div>
            <div className="hidden md:block">
              <h3 className="font-primary font-medium text-xs uppercase mb-l">{t("aul.unitDetails")}</h3>
            </div>
            <div>
              {attributes.map((attribute: string, index) => {
                return !!unit[attribute as keyof CommercetoolsUnit] && ((isDesktop && index != 0) || !isDesktop) ? (
                  <div className="even:bg-gray-7 flex justify-between gap-s" key={`aul-attribute-${attribute}`}>
                    <div data-attribute={attribute} className="font-secondary text-s py-s px-xxs md:px-0">
                      {t(`aul.unit.label.${attribute}`)}
                    </div>
                    <div className="uppercase font-primary font-medium text-right text-xs py-s px-xxs md:px-0">
                      {(() => {
                        // Handle Project (Priority: Translated name -> Original name)
                        if (attribute === "project") {
                          return translatedProjectName || String(unit[attribute as keyof CommercetoolsUnit] || "");
                        }

                        // Existing logic for rounding area values
                        if (["areaSqft", "areaSqmt"].includes(attribute)) {
                          const value = unit[attribute as keyof CommercetoolsUnit];
                          return value ? Math.round(parseFloat(value as string)) : "";
                        }

                        // Existing logic for rounding area values
                        if (["identifier"].includes(attribute)) {
                          const value =
                            t(`aul.unit.${unit[attribute as keyof CommercetoolsUnit]}.value`) ?? unit[attribute as keyof CommercetoolsUnit];
                          return value;
                        }

                        // Existing logic for rounding area values
                        if (["status"].includes(attribute)) {
                          const value =
                            t(`aul.unit.${unit[attribute as keyof CommercetoolsUnit]}.value`) ?? unit[attribute as keyof CommercetoolsUnit];
                          return value;
                        }

                        // Existing logic for rounding area values
                        if (["balcony"].includes(attribute)) {
                          const value =
                            t(`aul.unit.${unit[attribute as keyof CommercetoolsUnit]}.value`) ?? unit[attribute as keyof CommercetoolsUnit];
                          return value;
                        }

                        // Fallback for all other attributes
                        const rawValue = unit[attribute as keyof CommercetoolsUnit];

                        // If it's a renderable primitive, return as is
                        if (typeof rawValue === "string" || typeof rawValue === "number") {
                          return rawValue;
                        }

                        // If it's a Date, Array, or Object, convert to string to satisfy TypeScript
                        return rawValue ? String(rawValue) : "";
                      })()}
                    </div>
                  </div>
                ) : (
                  <Fragment key={`aul-attribute-${attribute}`} />
                );
              })}
            </div>
          </div>
          {/* {propertyType !== "Villa" && ( */}
          <div>
            <MagnifyContextProvider>
              <FloorPlan unit={unit} />
            </MagnifyContextProvider>
          </div>
          {/* )} */}
        </div>
        <div
          className={`flex flex-col md:flex-row md:items-center md:justify-between px-m md:px-[56px] bg-white md:bg-transparent sticky md:static stickyBarMobile gap-m md:py-m ${!isSo ? "border-t border-t-gray-10 bottom-0 pb-l" : ""}`}
        >
          {unit.sellable && (
            <div
              className={`w-[400px] ${isMobile ? "text-center" : ""} ${
                !(
                  t("unit.Agent.label") === "unit.Agent.label" ||
                  t("unit.Agent.title") === "unit.Agent.title" ||
                  t("unit.Agent.subtitle") === "unit.Agent.subtitle" ||
                  t("unit.Agent.link") === "unit.Agent.link"
                ) && showMicroComponentsFeatureFlag
                  ? "bg-white"
                  : ""
              } p-[4px] rounded`}
            >
              {!(
                t("unit.Agent.label") === "unit.Agent.label" ||
                t("unit.Agent.title") === "unit.Agent.title" ||
                t("unit.Agent.subtitle") === "unit.Agent.subtitle" ||
                t("unit.Agent.link") === "unit.Agent.link"
              ) &&
                showMicroComponentsFeatureFlag && (
                  <>
                    <p className="font-primary font-medium text-[12px] uppercase pl-[8px] mt-[4px]">{t("unit.Agent.title")}</p>
                    <p className="font-secondary font-medium text-[12px] pl-[8px] mt-[4px]">{t("unit.Agent.subtitle")}</p>
                    <p className="font-secondary font-medium text-[12px] pl-[8px] pr-[8px]">
                      {t("unit.Agent.label")}
                      <Button
                        className={`text-[12px] font-secondary font-medium pb-[8px] pl-[8px] underline`}
                        variant={ButtonVariant.TERTIARY}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.BLACK}
                        // href={props?.link?.id}
                        onClick={() => {
                          setIsDialogueOpen(true);
                        }}
                      >
                        {t("unit.Agent.link")}
                      </Button>
                    </p>
                  </>
                )}
            </div>
          )}
          {!isSo && (
            <div className="flex flex-col gap-m items-center justify-center md:flex-row md:items-center md:justify-end w-full md:w-auto">
              {isDesktop && desktopPanelID === "select-unit" ? (
                <div className="text-center">{isDesktop && <AddToFavorites item={unit} cta={true} />}</div>
              ) : (
                <>
                  <div className="text-center flex items-center justify-center mt-s md:mt-0 gap-xs share-button">
                    <button
                      onClick={() => share({ title: t("title.common.shareUnit"), text: unit.name, url: "" })}
                      className="underline text-s font-medium"
                    >
                      {t("common.label.shareUnit")}
                    </button>
                    <AddToFavorites item={unit} className="relative z-9  ml-auto hidden md:flex sm:hidden" fillColor="fill-black" />
                  </div>
                  <div className="text-center">
                    <Button
                      rounded
                      size={ButtonSize.MEDIUM}
                      theme={ButtonTheme.BLACK}
                      type="button"
                      variant={ButtonVariant.PRIMARY}
                      customClass="md:!px-xl w-[calc(100%-32px)]  mx-auto md:w-auto"
                      onClick={() => {
                        if (unit.sellable) {
                          handleReserve(unit.inventoryID, unit.id);
                        } else {
                          setEnquireModalOpen(true);
                          setGetFormPageLocation("Property Type Page Lead Form");
                        }
                      }}
                    >
                      {unit.sellable ? t("unit.reserve.now") : enquireLabel}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default UnitDetails;
